import { SkillDef, SkillBranch } from '../types';

export const SKILLS: SkillDef[] = [
  // ════════════════════════════════════════════
  // EXCAVATION BRANCH
  // ════════════════════════════════════════════

  // Tier 1
  {
    id: 'exc_quick_hands',
    name: 'Quick Hands',
    branch: 'excavation',
    tier: 1,
    cost: 1,
    description: 'Your experience with a trowel pays off. Digging is 20% faster.',
    effect: 'dig_speed:0.8',
  },
  {
    id: 'exc_gentle_touch',
    name: 'Gentle Touch',
    branch: 'excavation',
    tier: 1,
    cost: 1,
    description: 'You learn to feel for resistance changes. Artifact damage chance reduced by 15%.',
    effect: 'damage_reduce:0.15',
  },
  {
    id: 'exc_efficient_strokes',
    name: 'Efficient Strokes',
    branch: 'excavation',
    tier: 1,
    cost: 1,
    description: 'Waste less energy on each swing. Tool durability costs reduced by 10%.',
    effect: 'tool_cost:0.9',
  },
  {
    id: 'exc_sharp_eyes',
    name: 'Sharp Eyes',
    branch: 'excavation',
    tier: 1,
    cost: 1,
    description: 'You notice subtle color changes in the soil. Artifact tiles shimmer faintly when adjacent.',
    effect: 'adjacent_hint:1',
  },

  // Tier 2
  {
    id: 'exc_power_dig',
    name: 'Power Dig',
    branch: 'excavation',
    tier: 2,
    cost: 2,
    description: 'Channel your strength efficiently. Pickaxe digs through hard layers 30% faster.',
    effect: 'pickaxe_speed:0.7',
    prerequisite: 'exc_quick_hands',
  },
  {
    id: 'exc_brush_mastery',
    name: 'Brush Mastery',
    branch: 'excavation',
    tier: 2,
    cost: 2,
    description: 'Your brushwork is precise and deliberate. Brush speed doubled, no damage at all.',
    effect: 'brush_speed:0.5',
    prerequisite: 'exc_gentle_touch',
  },
  {
    id: 'exc_layer_reader',
    name: 'Layer Reader',
    branch: 'excavation',
    tier: 2,
    cost: 2,
    description: 'You can visually identify layer boundaries. Layer transitions are highlighted on the grid.',
    effect: 'layer_highlight:1',
    prerequisite: 'exc_sharp_eyes',
  },
  {
    id: 'exc_charge_expert',
    name: 'Charge Expert',
    branch: 'excavation',
    tier: 2,
    cost: 2,
    description: 'Better placement of excavation charges. Charge damage chance reduced by 50%.',
    effect: 'charge_damage:0.5',
    prerequisite: 'exc_efficient_strokes',
  },

  // Tier 3
  {
    id: 'exc_surgical_precision',
    name: 'Surgical Precision',
    branch: 'excavation',
    tier: 3,
    cost: 3,
    description: 'Your trowel work is almost artistic. Trowel damage chance drops to near zero.',
    effect: 'trowel_damage:0.01',
    prerequisite: 'exc_brush_mastery',
  },
  {
    id: 'exc_deep_drilling',
    name: 'Deep Drilling',
    branch: 'excavation',
    tier: 3,
    cost: 3,
    description: 'You have developed techniques for the hardest materials. Rock and bedrock dig speed improved by 40%.',
    effect: 'hard_layer_speed:0.6',
    prerequisite: 'exc_power_dig',
  },
  {
    id: 'exc_area_sweep',
    name: 'Area Sweep',
    branch: 'excavation',
    tier: 3,
    cost: 3,
    description: 'Clear topsoil from a 2x2 area in one action. Saves enormous time on upper layers.',
    effect: 'area_dig:2',
    prerequisite: 'exc_layer_reader',
  },
  {
    id: 'exc_controlled_blast',
    name: 'Controlled Blast',
    branch: 'excavation',
    tier: 3,
    cost: 3,
    description: 'Shaped charges that clear rock without touching artifacts. Charge damage reduced to 10%.',
    effect: 'charge_damage:0.1',
    prerequisite: 'exc_charge_expert',
  },

  // Tier 4
  {
    id: 'exc_master_excavator',
    name: 'Master Excavator',
    branch: 'excavation',
    tier: 4,
    cost: 4,
    description: 'The pinnacle of fieldwork. All digging is 50% faster with half the damage risk.',
    effect: 'dig_speed:0.5,damage_reduce:0.5',
    prerequisite: 'exc_surgical_precision',
  },
  {
    id: 'exc_earthwhisper',
    name: 'Earthwhisper',
    branch: 'excavation',
    tier: 4,
    cost: 4,
    description: 'You can feel artifacts through the soil. Tiles containing artifacts glow when you are within 2 tiles.',
    effect: 'artifact_sense:2',
    prerequisite: 'exc_area_sweep',
  },
  {
    id: 'exc_titan_drill',
    name: 'Titan Drill',
    branch: 'excavation',
    tier: 4,
    cost: 4,
    description: 'No layer can resist you. Bedrock and permafrost dig speed improved by 60%.',
    effect: 'hard_layer_speed:0.4',
    prerequisite: 'exc_deep_drilling',
  },
  {
    id: 'exc_demolition_artist',
    name: 'Demolition Artist',
    branch: 'excavation',
    tier: 4,
    cost: 4,
    description: 'Your charges clear a 5x5 area with zero artifact damage. Redefines large-scale excavation.',
    effect: 'charge_area:5,charge_damage:0.0',
    prerequisite: 'exc_controlled_blast',
  },

  // ════════════════════════════════════════════
  // ANALYSIS BRANCH
  // ════════════════════════════════════════════

  // Tier 1
  {
    id: 'ana_quick_clean',
    name: 'Quick Clean',
    branch: 'analysis',
    tier: 1,
    cost: 1,
    description: 'You have a knack for cleaning finds. Cleaning step is 25% faster.',
    effect: 'clean_speed:0.75',
  },
  {
    id: 'ana_keen_observer',
    name: 'Keen Observer',
    branch: 'analysis',
    tier: 1,
    cost: 1,
    description: 'Your eye for detail speeds up examination. Examine step is 20% faster.',
    effect: 'examine_speed:0.8',
  },
  {
    id: 'ana_budget_dating',
    name: 'Budget Dating',
    branch: 'analysis',
    tier: 1,
    cost: 1,
    description: 'You know which tests to skip. Carbon dating costs reduced by 20%.',
    effect: 'dating_cost:0.8',
  },
  {
    id: 'ana_field_notes',
    name: 'Field Notes',
    branch: 'analysis',
    tier: 1,
    cost: 1,
    description: 'Meticulous note-taking in the field. Documentation step is 20% faster.',
    effect: 'document_speed:0.8',
  },

  // Tier 2
  {
    id: 'ana_restoration',
    name: 'Restoration',
    branch: 'analysis',
    tier: 2,
    cost: 2,
    description: 'You can partially restore damaged artifacts. Damaged items improve one condition grade during cleaning.',
    effect: 'restore_grade:1',
    prerequisite: 'ana_quick_clean',
  },
  {
    id: 'ana_pattern_recognition',
    name: 'Pattern Recognition',
    branch: 'analysis',
    tier: 2,
    cost: 2,
    description: 'You spot stylistic connections quickly. Cross-referencing speed improved by 30%.',
    effect: 'crossref_speed:0.7',
    prerequisite: 'ana_keen_observer',
  },
  {
    id: 'ana_isotope_shortcut',
    name: 'Isotope Shortcut',
    branch: 'analysis',
    tier: 2,
    cost: 2,
    description: 'You know which isotope ratios matter most. Dating costs halved.',
    effect: 'dating_cost:0.5',
    prerequisite: 'ana_budget_dating',
  },
  {
    id: 'ana_auto_catalog',
    name: 'Auto-Catalog',
    branch: 'analysis',
    tier: 2,
    cost: 2,
    description: 'Your documentation system is so refined that artifacts are auto-cataloged on examination.',
    effect: 'auto_document:1',
    prerequisite: 'ana_field_notes',
  },

  // Tier 3
  {
    id: 'ana_master_restorer',
    name: 'Master Restorer',
    branch: 'analysis',
    tier: 3,
    cost: 3,
    description: 'Even fragments can be rebuilt. Artifacts improve up to two condition grades during cleaning.',
    effect: 'restore_grade:2',
    prerequisite: 'ana_restoration',
  },
  {
    id: 'ana_cultural_database',
    name: 'Cultural Database',
    branch: 'analysis',
    tier: 3,
    cost: 3,
    description: 'You maintain a personal database of cultural markers. Cross-referencing is automatic on examine.',
    effect: 'auto_crossref:1',
    prerequisite: 'ana_pattern_recognition',
  },
  {
    id: 'ana_rapid_dating',
    name: 'Rapid Dating',
    branch: 'analysis',
    tier: 3,
    cost: 3,
    description: 'Portable dating equipment lets you date artifacts in the field. Dating step takes half the time.',
    effect: 'dating_speed:0.5',
    prerequisite: 'ana_isotope_shortcut',
  },
  {
    id: 'ana_publish_ready',
    name: 'Publish-Ready',
    branch: 'analysis',
    tier: 3,
    cost: 3,
    description: 'Your documentation meets publication standards. Fully documented artifacts gain a 20% value bonus.',
    effect: 'documented_value:1.2',
    prerequisite: 'ana_auto_catalog',
  },

  // Tier 4
  {
    id: 'ana_miracle_worker',
    name: 'Miracle Worker',
    branch: 'analysis',
    tier: 4,
    cost: 4,
    description: 'You can reconstruct artifacts from almost nothing. Fragment-condition artifacts restore to fair.',
    effect: 'restore_grade:3',
    prerequisite: 'ana_master_restorer',
  },
  {
    id: 'ana_instant_analysis',
    name: 'Instant Analysis',
    branch: 'analysis',
    tier: 4,
    cost: 4,
    description: 'Your vast experience lets you identify artifacts on sight. All analysis steps are 50% faster.',
    effect: 'all_analysis_speed:0.5',
    prerequisite: 'ana_cultural_database',
  },
  {
    id: 'ana_free_dating',
    name: 'Free Dating',
    branch: 'analysis',
    tier: 4,
    cost: 4,
    description: 'Your university connections provide free lab access. Dating costs nothing.',
    effect: 'dating_cost:0.0',
    prerequisite: 'ana_rapid_dating',
  },
  {
    id: 'ana_definitive_work',
    name: 'Definitive Work',
    branch: 'analysis',
    tier: 4,
    cost: 4,
    description: 'Your publications are cited worldwide. Fully documented artifacts gain a 50% value bonus and extra reputation.',
    effect: 'documented_value:1.5,documented_rep:1.5',
    prerequisite: 'ana_publish_ready',
  },

  // ════════════════════════════════════════════
  // BUSINESS BRANCH
  // ════════════════════════════════════════════

  // Tier 1
  {
    id: 'biz_smooth_talker',
    name: 'Smooth Talker',
    branch: 'business',
    tier: 1,
    cost: 1,
    description: 'You know how to pitch a find. Museum sale prices increased by 10%.',
    effect: 'sale_price:1.1',
  },
  {
    id: 'biz_grant_writer',
    name: 'Grant Writer',
    branch: 'business',
    tier: 1,
    cost: 1,
    description: 'Your grant applications are compelling. Grant success chance increased by 15%.',
    effect: 'grant_chance:0.15',
  },
  {
    id: 'biz_crew_manager',
    name: 'Crew Manager',
    branch: 'business',
    tier: 1,
    cost: 1,
    description: 'You run a tighter operation. Crew wages reduced by 10%.',
    effect: 'crew_wage:0.9',
  },
  {
    id: 'biz_haggler',
    name: 'Haggler',
    branch: 'business',
    tier: 1,
    cost: 1,
    description: 'You drive a hard bargain on supplies. Permit and travel costs reduced by 10%.',
    effect: 'travel_cost:0.9,permit_cost:0.9',
  },

  // Tier 2
  {
    id: 'biz_museum_contacts',
    name: 'Museum Contacts',
    branch: 'business',
    tier: 2,
    cost: 2,
    description: 'You have friends in the museum world. Relationship gain with buyers is doubled.',
    effect: 'relationship_gain:2.0',
    prerequisite: 'biz_smooth_talker',
  },
  {
    id: 'biz_research_grant',
    name: 'Research Grant',
    branch: 'business',
    tier: 2,
    cost: 2,
    description: 'Secure a small recurring stipend. Receive $200 per dig day from grant funding.',
    effect: 'daily_income:200',
    prerequisite: 'biz_grant_writer',
  },
  {
    id: 'biz_volunteer_network',
    name: 'Volunteer Network',
    branch: 'business',
    tier: 2,
    cost: 2,
    description: 'Enthusiastic volunteers supplement your crew. One free crew member at every site.',
    effect: 'free_crew:1',
    prerequisite: 'biz_crew_manager',
  },
  {
    id: 'biz_bulk_discount',
    name: 'Bulk Discount',
    branch: 'business',
    tier: 2,
    cost: 2,
    description: 'Your supplier gives you a break on large orders. Tool costs reduced by 25%.',
    effect: 'tool_cost:0.75',
    prerequisite: 'biz_haggler',
  },

  // Tier 3
  {
    id: 'biz_exclusive_deal',
    name: 'Exclusive Deal',
    branch: 'business',
    tier: 3,
    cost: 3,
    description: 'Negotiate exclusive contracts. One chosen buyer pays 30% more for their preferred types.',
    effect: 'exclusive_bonus:1.3',
    prerequisite: 'biz_museum_contacts',
  },
  {
    id: 'biz_major_funding',
    name: 'Major Funding',
    branch: 'business',
    tier: 3,
    cost: 3,
    description: 'A prestigious foundation backs your work. Receive $500 per dig day from grant funding.',
    effect: 'daily_income:500',
    prerequisite: 'biz_research_grant',
  },
  {
    id: 'biz_expert_crew',
    name: 'Expert Crew',
    branch: 'business',
    tier: 3,
    cost: 3,
    description: 'Your reputation attracts top talent. Crew efficiency increased by 30%.',
    effect: 'crew_efficiency:1.3',
    prerequisite: 'biz_volunteer_network',
  },
  {
    id: 'biz_diplomatic_immunity',
    name: 'Diplomatic Immunity',
    branch: 'business',
    tier: 3,
    cost: 3,
    description: 'Your international reputation opens doors. Permit costs reduced by 40%.',
    effect: 'permit_cost:0.6',
    prerequisite: 'biz_bulk_discount',
  },

  // Tier 4
  {
    id: 'biz_auction_house',
    name: 'Auction House',
    branch: 'business',
    tier: 4,
    cost: 4,
    description: 'You gain access to elite auction houses. All sales earn 40% more, but relationship gain is halved.',
    effect: 'sale_price:1.4,relationship_gain:0.5',
    prerequisite: 'biz_exclusive_deal',
  },
  {
    id: 'biz_endowment',
    name: 'Endowment',
    branch: 'business',
    tier: 4,
    cost: 4,
    description: 'A permanent endowment funds your expeditions. Receive $1000 per dig day, eliminating money worries.',
    effect: 'daily_income:1000',
    prerequisite: 'biz_major_funding',
  },
  {
    id: 'biz_dream_team',
    name: 'Dream Team',
    branch: 'business',
    tier: 4,
    cost: 4,
    description: 'The world\'s best want to work with you. Two free expert crew members at every site.',
    effect: 'free_crew:2,crew_efficiency:1.5',
    prerequisite: 'biz_expert_crew',
  },
  {
    id: 'biz_open_borders',
    name: 'Open Borders',
    branch: 'business',
    tier: 4,
    cost: 4,
    description: 'Governments waive fees for your groundbreaking work. Travel costs eliminated, permits halved.',
    effect: 'travel_cost:0.0,permit_cost:0.5',
    prerequisite: 'biz_diplomatic_immunity',
  },

  // ════════════════════════════════════════════
  // INTUITION BRANCH
  // ════════════════════════════════════════════

  // Tier 1
  {
    id: 'int_cheap_scan',
    name: 'Cheap Scan',
    branch: 'intuition',
    tier: 1,
    cost: 1,
    description: 'You tweak the GPR to use less power. GPR scan cost reduced by 20%.',
    effect: 'gpr_cost:0.8',
  },
  {
    id: 'int_gut_feeling',
    name: 'Gut Feeling',
    branch: 'intuition',
    tier: 1,
    cost: 1,
    description: 'Something tells you where to dig. At season start, 2 random artifact tiles are hinted.',
    effect: 'start_hints:2',
  },
  {
    id: 'int_theory_student',
    name: 'Theory Student',
    branch: 'intuition',
    tier: 1,
    cost: 1,
    description: 'You study existing theories before arriving. Theory questions show one wrong answer eliminated.',
    effect: 'theory_eliminate:1',
  },
  {
    id: 'int_weather_sense',
    name: 'Weather Sense',
    branch: 'intuition',
    tier: 1,
    cost: 1,
    description: 'You can read the sky. Weather forecast shown 3 days ahead instead of 1.',
    effect: 'forecast_days:3',
  },

  // Tier 2
  {
    id: 'int_wide_scan',
    name: 'Wide Scan',
    branch: 'intuition',
    tier: 2,
    cost: 2,
    description: 'Modified GPR antenna scans a 3x3 area instead of a single tile.',
    effect: 'gpr_area:3',
    prerequisite: 'int_cheap_scan',
  },
  {
    id: 'int_dowsing_rod',
    name: 'Dowsing Rod',
    branch: 'intuition',
    tier: 2,
    cost: 2,
    description: 'Call it superstition, but it works. At season start, 4 artifact tiles are hinted.',
    effect: 'start_hints:4',
    prerequisite: 'int_gut_feeling',
  },
  {
    id: 'int_academic_network',
    name: 'Academic Network',
    branch: 'intuition',
    tier: 2,
    cost: 2,
    description: 'Colleagues share research notes. Two wrong theory answers eliminated.',
    effect: 'theory_eliminate:2',
    prerequisite: 'int_theory_student',
  },
  {
    id: 'int_storm_chaser',
    name: 'Storm Chaser',
    branch: 'intuition',
    tier: 2,
    cost: 2,
    description: 'You work through bad weather. Rain time penalty reduced by 50%.',
    effect: 'rain_penalty:0.5',
    prerequisite: 'int_weather_sense',
  },

  // Tier 3
  {
    id: 'int_deep_scan',
    name: 'Deep Scan',
    branch: 'intuition',
    tier: 3,
    cost: 3,
    description: 'Your GPR penetrates all layers. Scans reveal artifact depth in addition to position.',
    effect: 'gpr_depth:1',
    prerequisite: 'int_wide_scan',
  },
  {
    id: 'int_sixth_sense',
    name: 'Sixth Sense',
    branch: 'intuition',
    tier: 3,
    cost: 3,
    description: 'You feel the pull of history. Tiles adjacent to artifacts pulse faintly as you dig nearby.',
    effect: 'nearby_pulse:1',
    prerequisite: 'int_dowsing_rod',
  },
  {
    id: 'int_thesis_advisor',
    name: 'Thesis Advisor',
    branch: 'intuition',
    tier: 3,
    cost: 3,
    description: 'Your mentor reviews your theories. Theory submissions have a 25% chance to auto-correct if wrong.',
    effect: 'theory_safety:0.25',
    prerequisite: 'int_academic_network',
  },
  {
    id: 'int_all_weather',
    name: 'All-Weather Dig',
    branch: 'intuition',
    tier: 3,
    cost: 3,
    description: 'Specialized equipment lets you work in storms at reduced efficiency instead of not at all.',
    effect: 'storm_work:0.5',
    prerequisite: 'int_storm_chaser',
  },

  // Tier 4
  {
    id: 'int_satellite_gpr',
    name: 'Satellite GPR',
    branch: 'intuition',
    tier: 4,
    cost: 4,
    description: 'Orbital scanning reveals the entire grid at once. One free full-site scan per season.',
    effect: 'free_full_scan:1',
    prerequisite: 'int_deep_scan',
  },
  {
    id: 'int_archaeo_instinct',
    name: 'Archaeo-Instinct',
    branch: 'intuition',
    tier: 4,
    cost: 4,
    description: 'Years of experience become unconscious knowledge. All artifact tiles are revealed at season start.',
    effect: 'reveal_all:1',
    prerequisite: 'int_sixth_sense',
  },
  {
    id: 'int_grand_theory',
    name: 'Grand Theory',
    branch: 'intuition',
    tier: 4,
    cost: 4,
    description: 'Your theoretical framework is unassailable. Correct theory rewards doubled, wrong answers have no penalty.',
    effect: 'theory_reward:2.0,theory_no_penalty:1',
    prerequisite: 'int_thesis_advisor',
  },
  {
    id: 'int_climate_control',
    name: 'Climate Control',
    branch: 'intuition',
    tier: 4,
    cost: 4,
    description: 'Portable shelters and cooling systems negate all weather penalties. Every day is a perfect day.',
    effect: 'weather_immune:1',
    prerequisite: 'int_all_weather',
  },
];

export function getSkillDef(id: string): SkillDef | undefined {
  return SKILLS.find((skill) => skill.id === id);
}

export function getSkillsByBranch(branch: SkillBranch): SkillDef[] {
  return SKILLS.filter((skill) => skill.branch === branch);
}

export function getSkillsByTier(branch: SkillBranch, tier: number): SkillDef[] {
  return SKILLS.filter((skill) => skill.branch === branch && skill.tier === tier);
}
